import React from 'react';
import { TulipSettings } from '../types';

export interface TulipColorPreset {
  id: string;
  name: string;
  emoji: string;
  hex: string;
  className: string;
  vars: {
    '--gold-1': string;
    '--gold-2': string;
    '--gold-3': string;
    '--gold-4': string;
    '--gold-5': string;
  };
}

export const TULIP_COLOR_PRESETS: TulipColorPreset[] = [
  {
    id: 'golden',
    name: 'Amarillo Dorado',
    emoji: '💛',
    hex: '#f5b000',
    className: 'color-golden',
    vars: {
      '--gold-1': '#fff8cc',
      '--gold-2': '#ffe066',
      '--gold-3': '#f5b000',
      '--gold-4': '#cc8400',
      '--gold-5': '#8a5000',
    },
  },
  {
    id: 'ruby',
    name: 'Rojo Pasión',
    emoji: '❤️',
    hex: '#f50057',
    className: 'color-ruby',
    vars: {
      '--gold-1': '#ffebee',
      '--gold-2': '#ff80ab',
      '--gold-3': '#f50057',
      '--gold-4': '#c51162',
      '--gold-5': '#880e4f',
    },
  },
  {
    id: 'pink',
    name: 'Rosa Romántico',
    emoji: '💖',
    hex: '#eb2f96',
    className: 'color-pink',
    vars: {
      '--gold-1': '#fff0f6',
      '--gold-2': '#ffadd2',
      '--gold-3': '#eb2f96',
      '--gold-4': '#c41d7f',
      '--gold-5': '#780650',
    },
  },
  {
    id: 'lavender',
    name: 'Lavanda / Púrpura',
    emoji: '💜',
    hex: '#9254de',
    className: 'color-lavender',
    vars: {
      '--gold-1': '#f9f0ff',
      '--gold-2': '#d3adf7',
      '--gold-3': '#9254de',
      '--gold-4': '#722ed1',
      '--gold-5': '#391085',
    },
  },
  {
    id: 'peach',
    name: 'Durazno Suave',
    emoji: '🍑',
    hex: '#fa8c16',
    className: 'color-peach',
    vars: {
      '--gold-1': '#fff7e6',
      '--gold-2': '#ffd591',
      '--gold-3': '#fa8c16',
      '--gold-4': '#d46b08',
      '--gold-5': '#873800',
    },
  },
  {
    id: 'white',
    name: 'Blanco Perla',
    emoji: '🤍',
    hex: '#4ade80',
    className: 'color-white',
    vars: {
      '--gold-1': '#ffffff',
      '--gold-2': '#f0fdf4',
      '--gold-3': '#dcfce7',
      '--gold-4': '#86efac',
      '--gold-5': '#4ade80',
    },
  },
  {
    id: 'blue',
    name: 'Azul Zafiro',
    emoji: '💙',
    hex: '#0284c7',
    className: 'color-blue',
    vars: {
      '--gold-1': '#e0f2fe',
      '--gold-2': '#7dd3fc',
      '--gold-3': '#0284c7',
      '--gold-4': '#0369a1',
      '--gold-5': '#0c4a6e',
    },
  },
  {
    id: 'coral',
    name: 'Coral Radiante',
    emoji: '🧡',
    hex: '#ff4d4f',
    className: 'color-coral',
    vars: {
      '--gold-1': '#fff1f0',
      '--gold-2': '#ff85c0',
      '--gold-3': '#ff4d4f',
      '--gold-4': '#cf1322',
      '--gold-5': '#820014',
    },
  },
  {
    id: 'violet',
    name: 'Violeta Místico',
    emoji: '🔮',
    hex: '#9333ea',
    className: 'color-violet',
    vars: {
      '--gold-1': '#f3e8ff',
      '--gold-2': '#d8b4fe',
      '--gold-3': '#9333ea',
      '--gold-4': '#7e22ce',
      '--gold-5': '#581c87',
    },
  },
];

export const DEFAULT_TULIP_INDIVIDUAL_COLORS = ['golden', 'peach', 'pink', 'ruby'];

export function hexToHsl(hex: string): { h: number; s: number; l: number } {
  let c = hex.replace('#', '');
  if (c.length === 3) {
    c = c[0] + c[0] + c[1] + c[1] + c[2] + c[2];
  }
  const r = parseInt(c.substring(0, 2), 16) / 255;
  const g = parseInt(c.substring(2, 4), 16) / 255;
  const b = parseInt(c.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

export function hslToHex(h: number, s: number, l: number): string {
  h = ((h % 360) + 360) % 360;
  s = Math.max(0, Math.min(100, s)) / 100;
  l = Math.max(0, Math.min(100, l)) / 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;

  if (h >= 0 && h < 60) {
    r = c; g = x; b = 0;
  } else if (h >= 60 && h < 120) {
    r = x; g = c; b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0; g = c; b = x;
  } else if (h >= 180 && h < 240) {
    r = 0; g = x; b = c;
  } else if (h >= 240 && h < 300) {
    r = x; g = 0; b = c;
  } else if (h >= 300 && h < 360) {
    r = c; g = 0; b = x;
  }

  const toHex = (n: number) => {
    const hex = Math.round((n + m) * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function isValidHex(hex: string): boolean {
  return /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(hex);
}

export interface ColorFamily {
  id: string;
  name: string;
  emoji: string;
  colors: { name: string; hex: string }[];
}

export const COMPREHENSIVE_COLOR_FAMILIES: ColorFamily[] = [
  {
    id: 'reds',
    name: 'Rojos y Pasión',
    emoji: '🔴',
    colors: [
      { name: 'Rojo Pasión', hex: '#f50057' },
      { name: 'Rojo Carmesí', hex: '#dc2626' },
      { name: 'Rojo Rubí', hex: '#e11d48' },
      { name: 'Escarlata Vivo', hex: '#ff1744' },
      { name: 'Cereza Intensa', hex: '#be123c' },
      { name: 'Borgoña / Vino', hex: '#881337' },
      { name: 'Granate Real', hex: '#991b1b' },
      { name: 'Rojo Fresa', hex: '#f43f5e' },
    ],
  },
  {
    id: 'pinks',
    name: 'Rosas y Romance',
    emoji: '💖',
    colors: [
      { name: 'Rosa Romántico', hex: '#eb2f96' },
      { name: 'Rosa Pastel', hex: '#f472b6' },
      { name: 'Fucsia Neón', hex: '#d946ef' },
      { name: 'Rosa Chicle', hex: '#fb7185' },
      { name: 'Magenta Profundo', hex: '#c026d3' },
      { name: 'Rosa Nube', hex: '#fbcfe8' },
      { name: 'Rosa Flamenco', hex: '#ec4899' },
      { name: 'Orquídea Rosa', hex: '#f43f5e' },
    ],
  },
  {
    id: 'oranges',
    name: 'Naranjas y Cálidos',
    emoji: '🧡',
    colors: [
      { name: 'Durazno Suave', hex: '#fa8c16' },
      { name: 'Coral Radiante', hex: '#ff4d4f' },
      { name: 'Mandarina Viva', hex: '#f97316' },
      { name: 'Naranja Atardecer', hex: '#ea580c' },
      { name: 'Ámbar Cálido', hex: '#f59e0b' },
      { name: 'Salmón Delicado', hex: '#fb923c' },
      { name: 'Terracota Romántico', hex: '#c2410c' },
      { name: 'Albaricoque', hex: '#fdba74' },
    ],
  },
  {
    id: 'yellows',
    name: 'Amarillos y Sol',
    emoji: '💛',
    colors: [
      { name: 'Amarillo Dorado', hex: '#f5b000' },
      { name: 'Oro Puro', hex: '#eab308' },
      { name: 'Girasol Brillante', hex: '#facc15' },
      { name: 'Amarillo Canario', hex: '#fef08a' },
      { name: 'Miel Dulce', hex: '#d97706' },
      { name: 'Vainilla Suave', hex: '#fef9c3' },
      { name: 'Luz del Alba', hex: '#fde047' },
      { name: 'Mostaza Real', hex: '#ca8a04' },
    ],
  },
  {
    id: 'greens',
    name: 'Verdes y Naturaleza',
    emoji: '💚',
    colors: [
      { name: 'Esmeralda Floral', hex: '#10b981' },
      { name: 'Menta Fresca', hex: '#34d399' },
      { name: 'Verde Bosque', hex: '#059669' },
      { name: 'Verde Jade', hex: '#047857' },
      { name: 'Lima Brillante', hex: '#84cc16' },
      { name: 'Salvia Botánica', hex: '#6ee7b7' },
      { name: 'Pistacho Suave', hex: '#a3e635' },
      { name: 'Verde Trébol', hex: '#16a34a' },
    ],
  },
  {
    id: 'cyans',
    name: 'Turquesas y Celestes',
    emoji: '🩵',
    colors: [
      { name: 'Turquesa Caribe', hex: '#06b6d4' },
      { name: 'Aguamarina', hex: '#22d3ee' },
      { name: 'Cian Eléctrico', hex: '#00e5ff' },
      { name: 'Celeste Cielo', hex: '#38bdf8' },
      { name: 'Azul Glaciar', hex: '#7dd3fc' },
      { name: 'Menta Glacial', hex: '#a5f3fc' },
      { name: 'Laguna Azul', hex: '#0891b2' },
      { name: 'Brisa Marina', hex: '#bae6fd' },
    ],
  },
  {
    id: 'blues',
    name: 'Azules y Zafiros',
    emoji: '💙',
    colors: [
      { name: 'Azul Zafiro', hex: '#0284c7' },
      { name: 'Azul Real', hex: '#2563eb' },
      { name: 'Azul Océano', hex: '#0369a1' },
      { name: 'Marino Medianoche', hex: '#1e3a8a' },
      { name: 'Azul Cobalto', hex: '#1d4ed8' },
      { name: 'Índigo Mágico', hex: '#4338ca' },
      { name: 'Azul Eléctrico', hex: '#3b82f6' },
      { name: 'Azul Crepúsculo', hex: '#1e40af' },
    ],
  },
  {
    id: 'purples',
    name: 'Violetas y Morados',
    emoji: '💜',
    colors: [
      { name: 'Violeta Místico', hex: '#9333ea' },
      { name: 'Lavanda Enamorada', hex: '#9254de' },
      { name: 'Amatista Noble', hex: '#a855f7' },
      { name: 'Lila Delicado', hex: '#c084fc' },
      { name: 'Púrpura Imperial', hex: '#7e22ce' },
      { name: 'Berenjena Noche', hex: '#581c87' },
      { name: 'Brisa de Lavanda', hex: '#e9d5ff' },
      { name: 'Ciruela Encantada', hex: '#6b21a8' },
    ],
  },
  {
    id: 'pearls',
    name: 'Blanco Perla y Exóticos',
    emoji: '🤍',
    colors: [
      { name: 'Blanco Perla', hex: '#f0fdf4' },
      { name: 'Blanco Nieve', hex: '#ffffff' },
      { name: 'Crema Marfil', hex: '#fffbeb' },
      { name: 'Champán Dorado', hex: '#fef3c7' },
      { name: 'Oro Rosado', hex: '#fecdd3' },
      { name: 'Plata Luminosa', hex: '#e2e8f0' },
      { name: 'Carbón Suave', hex: '#334155' },
      { name: 'Obsidiana Terciopelo', hex: '#1e293b' },
    ],
  },
];

export function generateTulipShadesFromHex(hex: string): Record<string, string> {
  try {
    const { h, s, l } = hexToHsl(hex);
    // Dynamic lightness shading based on selected color's lightness
    const l1 = Math.min(98, Math.round(l + (100 - l) * 0.65)); // Highlight
    const l2 = Math.min(92, Math.round(l + (100 - l) * 0.35)); // Soft highlight
    const l4 = Math.max(8, Math.round(l * 0.65)); // Shadow
    const l5 = Math.max(4, Math.round(l * 0.38)); // Deep crease shadow

    return {
      '--gold-1': `hsl(${h}, ${Math.max(10, Math.min(s, 85))}%, ${l1}%)`,
      '--gold-2': `hsl(${h}, ${Math.max(15, Math.min(s, 95))}%, ${l2}%)`,
      '--gold-3': hex,
      '--gold-4': `hsl(${h}, ${Math.min(s + 5, 100)}%, ${l4}%)`,
      '--gold-5': `hsl(${h}, ${Math.min(s + 10, 100)}%, ${l5}%)`,
    };
  } catch {
    return {
      '--gold-1': '#fff8cc',
      '--gold-2': '#ffe066',
      '--gold-3': hex || '#f5b000',
      '--gold-4': '#cc8400',
      '--gold-5': '#8a5000',
    };
  }
}

export function getTulipStyle(colorIdOrHex: string): { className: string; style: React.CSSProperties } {
  if (!colorIdOrHex) {
    return { className: '', style: {} };
  }
  const preset = TULIP_COLOR_PRESETS.find((p) => p.id === colorIdOrHex);
  if (preset) {
    return {
      className: preset.className,
      style: preset.vars as unknown as React.CSSProperties,
    };
  }
  if (colorIdOrHex.startsWith('#')) {
    return {
      className: '',
      style: generateTulipShadesFromHex(colorIdOrHex) as unknown as React.CSSProperties,
    };
  }
  return { className: '', style: {} };
}

export function resolveTulipColor(
  tulipSettings: TulipSettings | undefined,
  index: number
): { className: string; style: React.CSSProperties; colorValue: string } {
  let colorValue = DEFAULT_TULIP_INDIVIDUAL_COLORS[index % DEFAULT_TULIP_INDIVIDUAL_COLORS.length];

  if (tulipSettings) {
    if (tulipSettings.mode === 'all') {
      colorValue = tulipSettings.allColor || 'golden';
    } else if (tulipSettings.individualColors && tulipSettings.individualColors[index]) {
      colorValue = tulipSettings.individualColors[index];
    }
  }

  const resolved = getTulipStyle(colorValue);
  return {
    ...resolved,
    colorValue,
  };
}
